import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Button, Snackbar, IconButton } from '@mui/material';
import { Hostage, Activity } from "../types";
import { decode } from '../../utils/Utils';
import CloseIcon from '@mui/icons-material/Close';
import '../../style/ActivityForm.css';
import { useUser } from "../../context/UserContext";

type Props = {
  open: boolean;
  onClose: () => void;
  hostage: Hostage;
  activity?: Activity;
};

const ActivityForm = ({ open, onClose, hostage, activity }: Props) => {
  const {user} = useUser();
  const { t, i18n } = useTranslation();
  const [photo, setPhoto] = useState<string | null>(null);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
  const [titleHe, setTitleHe] = useState(activity?.he?.title || '');
  const [titleEn, setTitleEn] = useState(activity?.en?.title || '');
  const [descriptionHe, setDescriptionHe] = useState(activity?.he?.description || '');
  const [descriptionEn, setDescriptionEn] = useState(activity?.en?.description || '');
  const [locationHe, setLocationHe] = useState(activity?.he?.location || '');
  const [locationEn, setLocationEn] = useState(activity?.en?.location || '');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isHebrew, setIsHebrew] = useState(true);
  const [isEnglish, setIsEnglish] = useState(false);

  const [isPublicActivity, setIsPublicActivity] = useState(false);
  const [isRecuringActivity, setIsRecuringActivity] = useState(false);
  const [notifyHe, setNotifyHe] = useState(false);
  const [notifyEn, setNotifyEn] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';

  useEffect(() => {
    if (activity) {
      setTitleHe(activity.he?.title ?? activity.title);
      setDescriptionHe(activity.he?.description ?? activity.description);
      setLocationHe(activity.he?.location ?? activity.location);
      setTitleEn(activity.en?.title);
      setDescriptionEn(activity.en?.description);
      setLocationEn(activity.en?.location);
      setPhoto(activity.photo);
      setIsPublicActivity(activity.isPublic);
      setIsRecuringActivity(activity.isRecuring);

      const activityDate = new Date(activity.date);
      const dateString = activityDate.toISOString().split('T')[0]; // Extracts the 'YYYY-MM-DD' part
      const timeString = activityDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      setDate(dateString);
      setTime(timeString);
    }
  }, [activity]);

  if (!hostage) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const base64Image = uploadedPhoto ? await getBase64Photo() : activity?.photo || null;
    const localDateTime = new Date(`${date}T${time}`);

    const activityData = {
      hostages: [hostage.id],
      type: 'activity',
      title: titleHe,
      description: descriptionHe,
      date: localDateTime < new Date() ? null : localDateTime,
      location: locationHe,
      photo: base64Image,
      isPublic: isPublicActivity,
      isRecuring: isRecuringActivity,
      he: {
        title: isHebrew ? titleHe : "",
        description: isHebrew ? descriptionHe : "",
        location: isHebrew ? locationHe: "",
        notify: notifyHe
      },
      en: {
        title: isEnglish ? titleEn : "",
        description: isEnglish ? descriptionEn : "",
        location: isEnglish ? locationEn : "",
        notify: notifyEn
      }
    };

    if (activity?.id) {
      await updateActivity(activity.id, activityData);
    } else {
      await createActivity(activityData);
    }
  };

  const resetForm = () => {
    setPhoto(null);
    setUploadedPhoto(null);
    setTitleHe('');
    setDescriptionHe('');
    setLocationHe('');
    setTitleEn('');
    setDescriptionEn('');
    setLocationEn('');
    setDate('');
    setTime('');
    setIsPublicActivity(false);
    setNotifyHe(false);
    setNotifyEn(false);
  };

  async function createActivity(activityData: any) {
    try {
      await fetch('/admin/activity', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-HOSTAGE-ID': activityData.hostages[0]
        },
        body: JSON.stringify({ activity: activityData }),
      });
      setSnackbarOpen(true);
      resetForm();
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error creating activity:', err);
    }
  }

  async function updateActivity(id: string, activityData: any) {
    try {
      await fetch(`/admin/activity/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-HOSTAGE-ID': activityData.hostages[0]
        },
        body: JSON.stringify({ activity: activityData }),
      });
      setSnackbarOpen(true);
      resetForm();
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error updating activity:', err);
    }
  }

  async function getBase64Photo() {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        resolve(event.target?.result?.toString() || '');
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(uploadedPhoto!);
    });
  }

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ style: { borderRadius: '10px 10px 0 0' } }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        style={{ position: 'absolute', right: 16, top: 16 }}  // Position the X button
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit}>
        <h2>{activity ? decode(t('createActivity.editTitle', { name: hostage[i18n.language as "en" | "he"].first_name })) : decode(t('createActivity.title', { name: hostage[i18n.language as "en" | "he"].first_name }))}</h2>
        <div style={{ display: "inline-flex", alignItems: "baseline" }}>
          <input
            style={{width: '30px'}}
            type="checkbox"
            checked={isHebrew}
            onChange={(event) => {
              setIsHebrew(event.target.checked);
            }}
          />
           <span style={{ paddingBottom: "2px" }}>עברית</span>
           <input
            style={{width: '30px'}}
            type="checkbox"
            checked={isEnglish}
            onChange={(event) => {
              setIsEnglish(event.target.checked);
            }}
          />
           <span style={{ paddingBottom: "2px" }}>English</span>
        </div>
        <div>
          <label htmlFor="uploadedPhoto">{t('createActivity.addImage')}</label>
          <input type="file" accept="image/*" onChange={(e) => setUploadedPhoto(e.target.files?.[0] || null)} />
        </div>

        {isHebrew && <div>
          <label htmlFor="title">{t('createActivity.activityNameHe')}</label>
          <input
            required
            type="text"
            id="title"
            value={titleHe}
            onChange={(e) => setTitleHe(e.target.value)}
            placeholder={t('createActivity.activityNamePlaceholder')}
          />
        </div>}

        {isEnglish && <div>
          <label htmlFor="title">{t('createActivity.activityNameEn')}</label>
          <input
            required
            type="text"
            id="title"
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
            placeholder={t('createActivity.activityNamePlaceholder')}
          />
        </div>}

        {isHebrew && <div>
          <label htmlFor="description">{t('createActivity.activityDescriptionHe')}</label>
          <textarea
            required
            id="description"
            value={descriptionHe}
            onChange={(e) => setDescriptionHe(e.target.value)}
            placeholder={t('createActivity.activityDescriptionPlaceholder')}
          />
        </div>}

        {isEnglish && <div>
          <label htmlFor="description">{t('createActivity.activityDescriptionEn')}</label>
          <textarea
            required
            id="description"
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
            placeholder={t('createActivity.activityDescriptionPlaceholder')}
          />
        </div>}

        {isHebrew && <div>
          <label htmlFor="location">{t('createActivity.activityLocationHe')}</label>
          <input
            type="text"
            id="location"
            value={locationHe}
            onChange={(e) => setLocationHe(e.target.value)}
            placeholder={t('createActivity.activityLocationPlaceholder')}
          />
        </div>}

        {isEnglish && <div>
          <label htmlFor="location">{t('createActivity.activityLocationEn')}</label>
          <input
            type="text"
            id="location"
            value={locationEn}
            onChange={(e) => setLocationEn(e.target.value)}
            placeholder={t('createActivity.activityLocationPlaceholder')}
          />
        </div>}

        <div>
          <label htmlFor="date">{t('createActivity.activityDate')}</label>
          <input
            type="date"
            id="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="time">{t('createActivity.activityTime')}</label>
          <input
            type="time"
            id="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>

        {isAdmin && <div style={{ display: "inline-flex", alignItems: "baseline" }}>
          <input
            style={{width: '30px'}}
            type="checkbox"
            checked={isPublicActivity}
            onChange={(event) => {
              setIsPublicActivity(event.target.checked);
            }}
          />
           <span style={{ paddingBottom: "2px" }}>{t("createActivity.shareWithAllHostages")}</span>
        </div>
        }

        {(isAdmin || isModerator) && (
          <div style={{ display: "inline-flex", alignItems: "baseline", marginTop: '10px' }}>
            <input
              style={{ width: '30px' }}
              type="checkbox"
              checked={isRecuringActivity}
              onChange={(event) => {
                setIsRecuringActivity(event.target.checked);
              }}
            />
            <span style={{ paddingBottom: "2px" }}>{t("createActivity.isRepeatingEvent")}</span> 
          </div>
        )}

        {isAdmin && isHebrew && (
          <div style={{ display: "inline-flex", alignItems: "baseline", marginTop: '10px' }}>
            <input
              style={{ width: '30px' }}
              type="checkbox"
              checked={notifyHe}
              onChange={(event) => {
                setNotifyHe(event.target.checked);
              }}
            />
            <span style={{ paddingBottom: "2px" }}>{t("createActivity.notifyHe")}</span> 
          </div>
        )}

        {isAdmin && isEnglish && (
          <div style={{ display: "inline-flex", alignItems: "baseline", marginTop: '10px' }}>
            <input
              style={{ width: '30px' }}
              type="checkbox"
              checked={notifyEn}
              onChange={(event) => {
                setNotifyEn(event.target.checked);
              }}
            />
            <span style={{ paddingBottom: "2px" }}>{t("createActivity.notifyEn")}</span> 
          </div>
        )}

        <Button className="submit-button" type="submit">
          {activity ? t('createActivity.submit') : t('createActivity.submit')}
        </Button>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={activity ? t('createActivity.successMessage') : t('createActivity.successMessage')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Drawer>
  );
};

export default ActivityForm;
